import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { logout } from '../services/auth';
import type { ApiUser } from '../services/auth';
import { getSession } from '../services/session';
import { useTheme } from './ThemeProvider';
import { CookieBanner } from './CookieBanner';
import { CookieSettingsModal } from './CookieSettingsModal';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<ApiUser | null>(null);
  const isAuth = location.pathname === '/login' || location.pathname === '/signup';
  const isHome = location.pathname === '/';
  const isApp = location.pathname === '/app';
  const { theme, setTheme } = useTheme();
  const [isCookieSettingsOpen, setIsCookieSettingsOpen] = useState(false);
  const [cookieReturnPath, setCookieReturnPath] = useState<string | null>(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Ignore logout errors, clear local state either way.
    }
    setUser(null);
    navigate('/');
  };

  useEffect(() => {
    let isMounted = true;
    const loadSession = async () => {
      try {
        const session = await getSession();
        if (isMounted) {
          setUser(session.user ?? null);
        }
      } catch {
        if (isMounted) {
          setUser(null);
        }
      }
    };
    loadSession();
    return () => {
      isMounted = false;
    };
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/cookie-settings') {
      setCookieReturnPath('/');
      setIsCookieSettingsOpen(true);
    }
  }, [location.pathname]);

  const openCookieSettings = () => {
    setCookieReturnPath(null);
    setIsCookieSettingsOpen(true);
  };

  const closeCookieSettings = () => {
    setIsCookieSettingsOpen(false);
    if (cookieReturnPath) {
      navigate(cookieReturnPath);
      setCookieReturnPath(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background font-sans text-text">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-surface/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 grid grid-cols-[auto_1fr_auto] items-center gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3 text-2xl font-bold tracking-tight text-text hover:text-primary transition-colors">
              <img src="/logo.svg" alt="20 Minutes Rule" className="h-9 w-9" />
              <span>20 Minutes Rule</span>
            </Link>
          </div>

          <div className="justify-self-center">
            {isHome && (
              <nav className="hidden md:flex items-center gap-4 text-sm font-medium text-muted">
                <a href="#how-it-works" className="hover:text-text">How it works</a>
                <a href="#faq" className="hover:text-text">FAQ</a>
                <a href="#contact" className="hover:text-text">Contact</a>
                <Link to="/buy-me-a-coffee" className="hover:text-text">Buy me a Coffee</Link>
              </nav>
            )}
          </div>

          <div className="flex items-center justify-end gap-4">
            {user ? (
              <div className="flex items-center gap-3">
                {!isApp && (
                  <Link to="/app">
                    <Button variant="ghost" size="sm">App</Button>
                  </Link>
                )}
                <Button variant="outline" size="sm" onClick={handleLogout}>Log out</Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                 {!isAuth && (
                    <>
                      <Link to="/login">
                        <Button variant="ghost" size="sm">Log in</Button>
                      </Link>
                      <Link to="/signup">
                        <Button variant="primary" size="sm">Sign up</Button>
                      </Link>
                    </>
                 )}
              </div>
            )}
            <button
              type="button"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-surface text-muted hover:text-text hover:border-primary/60 transition-colors"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 3v2M12 19v2M5.64 5.64l1.41 1.41M16.95 16.95l1.41 1.41M3 12h2M19 12h2M5.64 18.36l1.41-1.41M16.95 7.05l1.41-1.41" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                  <path d="M21 14.5A8.5 8.5 0 1 1 9.5 3a7 7 0 1 0 11.5 11.5Z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-surface border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
             <div>
               <h3 className="text-sm font-semibold text-text tracking-wider uppercase mb-4">Product</h3>
               <ul className="space-y-3">
                 <li><Link to="/app" className="text-base text-muted hover:text-primary">App</Link></li>
                 <li><Link to="/#how-it-works" className="text-base text-muted hover:text-primary">How it works</Link></li>
                 <li><Link to="/buy-me-a-coffee" className="text-base text-muted hover:text-primary">Buy me a Coffee</Link></li>
               </ul>
             </div>
             <div>
               <h3 className="text-sm font-semibold text-text tracking-wider uppercase mb-4">Legal</h3>
               <ul className="space-y-3">
                 <li><Link to="/privacy" className="text-base text-muted hover:text-primary">Privacy Policy</Link></li>
                 <li><Link to="/terms" className="text-base text-muted hover:text-primary">Terms & Conditions</Link></li>
                 <li><Link to="/cookie-policy" className="text-base text-muted hover:text-primary">Cookie Policy</Link></li>
                 <li><Link to="/imprint" className="text-base text-muted hover:text-primary">Imprint</Link></li>
               </ul>
             </div>
             <div>
               <h3 className="text-sm font-semibold text-text tracking-wider uppercase mb-4">Support</h3>
               <ul className="space-y-3">
                 <li><Link to="/#contact" className="text-base text-muted hover:text-primary">Contact</Link></li>
                 <li>
                   <button
                     type="button"
                     onClick={openCookieSettings}
                     className="text-base text-muted hover:text-primary"
                   >
                     Cookie Settings
                   </button>
                 </li>
                 <li><Link to="/accessibility" className="text-base text-muted hover:text-primary">Accessibility</Link></li>
               </ul>
             </div>
          </div>
          <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-base text-muted">&copy; {new Date().getFullYear()} 20 Minutes Rule. All rights reserved.</p>
            <div className="flex space-x-6">
                <span className="text-muted text-sm">Clean. Simple. Focused.</span>
            </div>
          </div>
        </div>
      </footer>

      <CookieBanner onOpenSettings={openCookieSettings} />
      <CookieSettingsModal isOpen={isCookieSettingsOpen} onClose={closeCookieSettings} />
    </div>
  );
};
